import { useState, useContext, useEffect } from "react";

// @mui core components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import AddIcon from "@mui/icons-material/Add";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
// Soft UI Dashboard PRO React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiAvatar from "components/SuiAvatar";
import SuiBadge from "components/SuiBadge";

import toast from "react-hot-toast";
import moment from "moment";
import { useForm, Controller } from "react-hook-form";

import { storeTags } from "utils/data";
import { capitalCase } from "capital-case";
// Images
import ImgCrop from "antd-img-crop";
import { Upload } from "antd";
import { getBase64, dataURLtoFile, beforeUpload } from "utils/imageHelper";
import curved0 from "assets/images/curved-images/curved0.jpg";

import { StoreContext } from "context/store";
import { AccountContext } from "context/account";
import { storeTypeOptions, employeeOptions } from "utils/data";
import api from "services/api";
import { nanoid } from "nanoid";
import { ErrorMessage } from "@hookform/error-message";

import PhoneInput from "react-phone-number-input";
import { isPossiblePhoneNumber, formatPhoneNumberIntl } from "react-phone-number-input";
import { countries as countryData } from "utils/countries";
import { usaStates } from "utils/data";

const countryOptions = countryData.map((c) => ({
  label: c.name,
  value: c.alpha2.toLocaleLowerCase(),
}));

const usaStateOptions = usaStates.map((x) => {
  return {
    label: x.abbreviation,
    value: x.abbreviation,
  };
});

const TAG_MAX_LENGTH = 25;
const TAG_MAX_QUANTITY = 20;
const STORE_DESCRIPTION_MAX_LENGTH = 2000; //long description
const MISSION_STATEMENT_MAX_LENGTH = 150; //short description
const STORE_NAME_MAX_LENGTH = 50;

const CompanyInfo = ({ onNext, onPrev, isNextDisabled, isPrevDisabled, saveNext }) => {
  const [avatarImageUrl, setAvatarImageUrl] = useState("");
  const [bgImageUrl, setBgImageUrl] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [newTagVisible, setNewTagVisible] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [tagOptions, setTagOptions] = useState(storeTags);

  const [shortDesc, setShortDesc] = useState("");
  const [longDesc, setLongDesc] = useState("");
  const [storeName, setStoreName] = useState("");
  const [phone, setPhone] = useState();
  const [countryCode, setCountryCode] = useState({});

  const account = useContext(AccountContext);

  const { store, updateStore } = useContext(StoreContext);

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    watch,
    getValues,
    reset,
    formState: { errors },
    control,
  } = useForm();

  useEffect(() => {
    const tags = store?.metadata?.store_tags || [];

    const initialOptions = tags.map((x) => ({ label: x, value: x }));
    setSelectedTags(initialOptions);
    setTagOptions([...storeTags, ...initialOptions]);

    const country_code = store?.metadata?.address?.country_code;
    let province = store?.metadata?.address?.province;

    // reset business phone number
    setValue("business_phone", store?.metadata?.business_phone);
    setPhone(store?.metadata?.business_phone);

    // reset country code

    const record = countryData.find((x) => x.alpha2.toLowerCase() == country_code);
    const countryOption = record?.name
      ? {
          label: record?.name,
          value: record?.alpha2.toLowerCase(),
        }
      : undefined;
    setCountryCode(countryOption);
    // reset state code
    if (country_code == "us") {
      const state = usaStateOptions.find((x) => x.value.toUpperCase() == province);

      province = state;
    }

    reset({
      name: store.name,
      detail: store.detail,
      story: store?.story,
      tags: initialOptions,
      email: account?.email,
      business_phone: store?.metadata?.business_phone,
      address_1: store?.metadata?.address?.address_1,
      address_2: store?.metadata?.address?.address_2,
      city: store?.metadata?.address?.city,
      province: province,
      country_code: countryOption,
      postal_code: store?.metadata?.address?.postal_code,
      instagram_link: store?.metadata?.instagram_link,
      company_link: store?.metadata?.company_link,
      facebook_link: store?.metadata?.facebook_link,
      twitter_link: store?.metadata?.twitter_link,
    });
    setShortDesc(store.detail);
    setLongDesc(store?.story);
    setStoreName(store.name);
    setAvatarImageUrl(store?.metadata?.avatar);
    setBgImageUrl(store?.metadata?.background_image);
  }, [store]);

  const handleSave = async (data) => {
    let avatarUrl = avatarImageUrl;
    let bgUrl = bgImageUrl;

    if (avatarImageUrl?.startsWith("data")) {
      const file = dataURLtoFile(avatarImageUrl, nanoid());

      const uploadedImg = await api.uploads.create([file]).then(({ data }) => {
        const uploaded = data.uploads.map(({ url }) => url);
        return uploaded;
      });
      avatarUrl = uploadedImg && uploadedImg.length > 0 ? uploadedImg[0] : "";
    }

    if (bgImageUrl?.startsWith("data")) {
      const file = dataURLtoFile(bgImageUrl, nanoid());

      const uploadedImg = await api.uploads.create([file]).then(({ data }) => {
        const uploaded = data.uploads.map(({ url }) => url);
        return uploaded;
      });
      bgUrl = uploadedImg && uploadedImg.length > 0 ? uploadedImg[0] : "";
    }
    /*
    const phoneNum = formatPhoneNumberIntl(data["business_phone"]);
    if (!isPossiblePhoneNumber(phoneNum)) {
      return toast.error("Not a valid phone number!");
    }
*/
    try {
      const payload = {
        metadata: {
          avatar: avatarUrl,
          background_image: bgUrl,
          store_tags: data["tags"].map((t) => t.value),
          address: {
            address_1: data["address_1"],
            address_2: data["address_2"],
            province:
              data?.province && typeof data["province"] == "object"
                ? data["province"].value
                : data["province"],
            city: data["city"],
            postal_code: data["postal_code"],
            country_code:
              data?.country_code && typeof data["country_code"] == "object"
                ? data["country_code"].value
                : data["country_code"],
          },
          business_phone: data["business_phone"],
          instagram_link: data["instagram_link"],
          company_link: data["company_link"],
          facebook_link: data["facebook_link"],
          twitter_link: data["twitter_link"],
        },
        name: data["name"],
        detail: data["detail"],
        story: data["story"],
      };

      await updateStore(payload);
      toast.success("Update company general information successfully!");
      saveNext();
    } catch (err) {
      console.log(err);
      toast.error("Update company general information failed!");
    }
  };

  const handleUploadImages = (data) => {
    return getBase64(data.file);
    // return api.uploads.create([file])
  };

  const handleCropImgOk = async (data) => {
    try {
      const url = await getBase64(data);
      var img = new Image();

      img.onload = resizeImage;
      img.src = url;

      function resizeImage() {
        var canvas = document.createElement("canvas");
        canvas.width = 360;
        canvas.height = 360;
        var ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, 360, 360);
        const newDataUri = canvas.toDataURL();

        setAvatarImageUrl(newDataUri);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleCropBackgroundImgOk = async (data) => {
    try {
      const url = await getBase64(data);
      setBgImageUrl(url);
    } catch (err) {
      console.log(err);
    }
  };

  //----------------------------------------------------------------
  //Handle tags
  //----------------------------------------------------------------
  const handleChangeTags = (option) => {
    if (option.length > TAG_MAX_QUANTITY) {
      return toast.error(`You can only have at most ${TAG_MAX_QUANTITY} tags!`);
    }
    setSelectedTags(option);
    setValue("tags", option);
  };

  const handleShowNewTag = () => {
    setNewTag("");
    setNewTagVisible(true);
  };

  const handleCloseNewTag = () => {
    setNewTagVisible(false);
  };

  const handleNewTagChange = (e) => {
    if (e.target.value) {
      const value = e.target.value.toUpperCase();
      setNewTag(value);
    } else {
      setNewTag("");
    }
  };

  const handleSaveNewTag = async (e) => {
    e.preventDefault();
    if (selectedTags.length >= TAG_MAX_QUANTITY) {
      return toast.error(`You can only have at most ${TAG_MAX_QUANTITY} tags!`);
    }
    const newTagTrimed = newTag.trim();
    if (!newTagTrimed) {
      return toast.error(`Your input tag is empty!`);
    }

    const inputTag = tagOptions.find((x) => x.value == newTagTrimed);
    const selectInputTag = selectedTags.find((x) => x.value == newTagTrimed);

    if (inputTag && inputTag) {
      if (!selectInputTag) {
        const newSelectedTags = [...selectedTags, { label: newTagTrimed, value: newTagTrimed }];
        handleChangeTags(newSelectedTags);
        handleCloseNewTag();
      } else {
        toast.error(`Tag: ${newTagTrimed} already exists in list!`);
      }

      return;
    }

    const newSelectedTags = [...selectedTags, { label: newTagTrimed, value: newTagTrimed }];

    handleChangeTags(newSelectedTags);
    setTagOptions([...storeTags, ...newSelectedTags]);
    handleCloseNewTag();
  };

  const handleChangePhoneNumber = (value) => {
    setValue("business_phone", value);
    setPhone(value);
  };

  const handleChangeCountry = (option) => {
    setCountryCode(option);

    setValue("country_code", option);
    let province = getValues("province");
    if (!option) {
      setValue("province", null);
      return;
    }

    if (option.value != "us") {
      if (province && typeof province == "object") {
        setValue("province", province.value);
      }
    } else if (option.value == "us" && province) {
      const provinceOpt = usaStateOptions.find((x) => x.value == province);
      if (provinceOpt && provinceOpt.value) {
        setValue("province", provinceOpt);
      } else {
        setValue("province", null);
      }
    }
  };

  const handlePhoneCountryChange = (country) => {
    const record = countryData.find((x) => x.alpha2 == country);
    const countryOption = {
      label: record?.name,
      value: record?.alpha2.toLowerCase(),
    };
    if (countryOption && countryOption.value) {
      handleChangeCountry(countryOption);
    }
  };

  return (
    <div>
      <SuiBox component="form" onSubmit={handleSubmit(handleSave)}>
        <Card id="company-info" sx={{ overflow: "visible" }} className="my-2">
          <SuiBox p={3}>
            <SuiTypography variant="h5">Company Information</SuiTypography>
          </SuiBox>
          <SuiBox pb={3} px={3}>
            <Grid container spacing={3} className="items-start">
              <Grid item xs={12} sm={6}>
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  height="100%"
                >
                  <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                    <SuiTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      textTransform="capitalize"
                      required
                    >
                      Business name
                    </SuiTypography>
                  </SuiBox>
                  <SuiInput
                    placeholder="Company Name"
                    required
                    {...register("name", {
                      required: true,
                      maxLength: {
                        value: STORE_NAME_MAX_LENGTH,
                        message: `Maxmium length should not exceed ${STORE_NAME_MAX_LENGTH} characters`,
                      },
                      value: storeName,
                      onChange: (e) => {
                        setStoreName(e.target.value);
                      },
                    })}
                  />
                  <div className="flex justify-between">
                    <div>
                      <ErrorMessage
                        errors={errors}
                        name="name"
                        render={({ message }) => (
                          <SuiTypography
                            variant="text"
                            fontWeight="regular"
                            fontSize="12px"
                            color="error"
                            className="ml-2"
                          >
                            {message}
                          </SuiTypography>
                        )}
                      />
                    </div>
                    <div className="text-sm text-gray-400">
                      {storeName ? storeName.length : 0}/{STORE_NAME_MAX_LENGTH}
                    </div>
                  </div>
                </SuiBox>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  height="100%"
                >
                  <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                    <SuiTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      textTransform="capitalize"
                    >
                      Avatar
                    </SuiTypography>
                  </SuiBox>
                  <div className="flex justify-between">
                    <ImgCrop
                      rotate
                      shape="round"
                      onModalOk={handleCropImgOk}
                      cropperProps={{
                        cropSize: { width: 50, height: 50 },
                      }}
                    >
                      <Upload
                        name="avatar"
                        className="hover:cursor-pointer flex items-center ml-1"
                        showUploadList={false}
                        customRequest={handleUploadImages}
                        beforeUpload={beforeUpload}
                      >
                        <Tooltip title={"Click to change"} placement="top">
                          <SuiAvatar
                            src={avatarImageUrl}
                            alt="profile-image"
                            variant="circle"
                            size="sm"
                            shadow="sm"
                          />
                        </Tooltip>
                      </Upload>
                    </ImgCrop>
                    <div className="flex gap-1">
                      {store?.metadata?.avatar != avatarImageUrl ? (
                        <SuiButton
                          variant="gradient"
                          color="light"
                          sx={{ height: "100%" }}
                          onClick={() => setAvatarImageUrl(store?.metadata?.avatar)}
                        >
                          reset
                        </SuiButton>
                      ) : null}

                      <ImgCrop rotate shape="round" onModalOk={handleCropImgOk}>
                        <Upload
                          name="avatar"
                          className="hover:cursor-pointer flex items-center"
                          showUploadList={false}
                          customRequest={handleUploadImages}
                          beforeUpload={beforeUpload}
                        >
                          <SuiButton variant="gradient" color="dark">
                            Upload
                          </SuiButton>
                        </Upload>
                      </ImgCrop>
                    </div>
                  </div>
                </SuiBox>
              </Grid>
              <Grid item xs={12} sm={12}>
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  height="100%"
                >
                  <SuiBox ml={0.5} mb={1} lineHeight={0} display="inline-block">
                    <SuiTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      textTransform="capitalize"
                    >
                      Mission Statement
                    </SuiTypography>
                  </SuiBox>

                  <SuiInput
                    placeholder={`Write a brief 1 or 2 sentence summary of what your company can offer your client base.(max ${MISSION_STATEMENT_MAX_LENGTH} characters)`}
                    rows={2}
                    multiline={true}
                    {...register(`detail`, {
                      maxLength: MISSION_STATEMENT_MAX_LENGTH,
                      value: shortDesc,
                      onChange: (e) => {
                        setShortDesc(e.target.value);
                      },
                    })}
                  />
                  <div className="flex justify-between">
                    <div>
                      {errors.detail?.type === "maxLength" ? (
                        <SuiTypography
                          variant="text"
                          fontWeight="regular"
                          fontSize="12px"
                          color="error"
                          className="ml-2"
                        >
                          Maxmium length should not exceed {MISSION_STATEMENT_MAX_LENGTH} characters
                        </SuiTypography>
                      ) : (
                        ""
                      )}
                    </div>
                    <div className="text-sm text-gray-400">
                      {shortDesc ? shortDesc.length : 0}/{MISSION_STATEMENT_MAX_LENGTH}
                    </div>
                  </div>
                </SuiBox>
              </Grid>
              <Grid item xs={12} sm={12}>
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  height="100%"
                >
                  <SuiBox ml={0.5} mb={1} lineHeight={0} display="inline-block">
                    <SuiTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      textTransform="capitalize"
                    >
                      Company Description
                    </SuiTypography>
                  </SuiBox>

                  <SuiInput
                    placeholder="This is your opportunity to tell your company's story and explain what you can
                bring to the table. Be as detailed as you would like."
                    rows={6}
                    multiline={true}
                    fullWidth={true}
                    {...register(`story`, {
                      maxLength: STORE_DESCRIPTION_MAX_LENGTH,
                      value: longDesc,
                      onChange: (e) => {
                        setLongDesc(e.target.value);
                      },
                    })}
                  />
                  <div className="flex justify-between">
                    <div>
                      {errors.story?.type === "maxLength" ? (
                        <SuiTypography
                          variant="text"
                          fontWeight="regular"
                          fontSize="12px"
                          color="error"
                          className="ml-2"
                        >
                          Maxmium length should not exceed {STORE_DESCRIPTION_MAX_LENGTH} characters
                        </SuiTypography>
                      ) : (
                        ""
                      )}
                    </div>
                    <div className="text-sm text-gray-400">
                      {longDesc ? longDesc.length : 0}/{STORE_DESCRIPTION_MAX_LENGTH}
                    </div>
                  </div>
                </SuiBox>
              </Grid>
              {/* <Grid item xs={12} sm={12}>
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  height="100%"
                >
                  <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                    <SuiTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      textTransform="capitalize"
                    >
                      Tags
                    </SuiTypography>
                  </SuiBox>
                  <div className="flex items-center">
                    <div className="grow">
                      <Tooltip
                        title={`You can select at most ${TAG_MAX_QUANTITY} tags!`}
                        placement="top"
                      >
                        <Controller
                          name="tags"
                          control={control}
                          render={({ field }) => {
                            return (
                              <SuiSelect
                                isMulti
                                closeMenuOnSelect={false}
                                {...field}
                                onChange={handleChangeTags}
                                options={tagOptions}
                              />
                            );
                          }}
                        />
                      </Tooltip>
                    </div>
                    <div>
                      <SuiButton
                        variant="gradient"
                        onClick={handleShowNewTag}
                        className="mx-2"
                        color="dark"
                      >
                        <AddIcon /> <span>Customize</span>
                      </SuiButton>
                    </div>
                  </div>
                </SuiBox>
              </Grid>
              <Grid item xs={12} sm={12}>
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  height="100%"
                >
                  <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                    <SuiTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      textTransform="capitalize"
                    >
                      Background Image
                    </SuiTypography>
                  </SuiBox>
                  <SuiBox
                    display="flex"
                    alignItems="center"
                    position="relative"
                    minHeight="18.75rem"
                    borderRadius="xl"
                    sx={{
                      backgroundImage: bgImageUrl
                        ? `url(${bgImageUrl})`
                        : ({ functions: { rgba, linearGradient }, palette: { gradients } }) =>
                            `${linearGradient(
                              rgba(gradients.secondary.main, 0.2),
                              rgba(gradients.secondary.state, 0.4)
                            )}`,
                      backgroundSize: "cover",
                      backgroundPosition: "50%",
                      overflow: "hidden",
                    }}
                  />
                </SuiBox>
                <div className="flex items-center mt-2 justify-center">
                  <div className="flex gap-1">
                    {store?.metadata?.background_image != bgImageUrl ? (
                      <SuiButton
                        variant="gradient"
                        color="light"
                        sx={{ height: "100%" }}
                        onClick={() => setBgImageUrl(store?.metadata?.background_image)}
                      >
                        reset
                      </SuiButton>
                    ) : null}
                    <ImgCrop rotate shape="rect" aspect={3} onModalOk={handleCropBackgroundImgOk}>
                      <Upload
                        name="avatar"
                        showUploadList={false}
                        customRequest={handleUploadImages}
                        beforeUpload={beforeUpload}
                      >
                        <div className="text-center h-full flex">
                          <SuiButton variant="gradient" color="dark">
                            Upload
                          </SuiButton>
                        </div>
                      </Upload>
                    </ImgCrop>
                  </div>
                </div>
              </Grid> */}
            </Grid>
            {newTagVisible ? (
              <Dialog
                open={newTagVisible}
                onClose={handleCloseNewTag}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                maxWidth="sm"
                fullWidth={true}
              >
                <DialogTitle id="alert-dialog-title">Create your customized tag</DialogTitle>

                <DialogContent>
                  <DialogContentText id="alert-dialog-description">
                    <SuiBox
                      display="flex"
                      flexDirection="column"
                      justifyContent="flex-end"
                      height="100%"
                    >
                      <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                        <SuiTypography
                          component="label"
                          variant="caption"
                          fontWeight="bold"
                          textTransform="capitalize"
                        >
                          New Tag
                        </SuiTypography>
                      </SuiBox>

                      <SuiInput
                        name="new-tag"
                        required
                        value={newTag}
                        placeholder={`Maxmuim ${TAG_MAX_LENGTH} characters`}
                        componentsProps={{ input: { maxlength: TAG_MAX_LENGTH } }}
                        onChange={handleNewTagChange}
                        maxLength={TAG_MAX_LENGTH}
                      />
                    </SuiBox>
                    <SuiBox display="flex" justifyContent="space-between">
                      <div>
                        {newTag.length > TAG_MAX_LENGTH ? (
                          <SuiTypography
                            variant="text"
                            fontWeight="regular"
                            fontSize="12px"
                            color="error"
                            sx={{ ml: 2 }}
                          >
                            Maxmium length should not exceed {TAG_MAX_LENGTH} characters
                          </SuiTypography>
                        ) : (
                          ""
                        )}
                      </div>
                      <SuiTypography
                        variant="text"
                        color="secondary"
                        fontWeight="regular"
                        fontSize="14px"
                      >
                        {newTag ? newTag.length : 0}/{TAG_MAX_LENGTH}
                      </SuiTypography>
                    </SuiBox>
                  </DialogContentText>
                </DialogContent>

                <DialogActions>
                  <SuiButton
                    variant="gradient"
                    size="medium"
                    onClick={handleCloseNewTag}
                    className="mr-2"
                    color="light"
                  >
                    Cancel
                  </SuiButton>
                  <SuiButton
                    size="medium"
                    variant="gradient"
                    color="dark"
                    onClick={handleSaveNewTag}
                  >
                    Create
                  </SuiButton>
                </DialogActions>
              </Dialog>
            ) : null}
          </SuiBox>
        </Card>
        <Card id="contact-info" sx={{ overflow: "visible" }} className="my-2">
          <SuiBox p={3}>
            <SuiTypography variant="h5">Contact Info</SuiTypography>
          </SuiBox>
          <SuiBox pb={3} px={3}>
            <Grid container spacing={3} className="items-start">
              <Grid item xs={12} sm={6}>
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  height="100%"
                >
                  <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                    <SuiTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      textTransform="capitalize"
                    >
                      Email
                    </SuiTypography>
                  </SuiBox>
                  <SuiInput
                    placeholder="Email"
                    readOnly
                    disabled
                    {...register("email", { required: true })}
                  />
                </SuiBox>
              </Grid>

              <Grid item xs={12} sm={6}>
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  height="100%"
                >
                  <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                    <SuiTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      textTransform="capitalize"
                    >
                      Phone
                    </SuiTypography>
                  </SuiBox>

                  <PhoneInput
                    label="Phone"
                    className="upstyled-phone-input h-10 px-2 rounded-lg"
                    placeholder="Enter phone number"
                    name="business_phone"
                    value={phone}
                    defaultCountry="US"
                    onCountryChange={handlePhoneCountryChange}
                    {...register("business_phone")}
                    onChange={handleChangePhoneNumber}
                  />
                </SuiBox>
              </Grid>

              <Grid item xs={12} sm={12}>
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  height="100%"
                >
                  <SuiBox ml={0.5} lineHeight={0} display="inline-block">
                    <SuiTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      textTransform="capitalize"
                    >
                      Street address
                    </SuiTypography>
                  </SuiBox>
                  <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                    <SuiTypography
                      component="label"
                      variant="caption"
                      fontWeight="regular"
                      color="text"
                    >
                      You street address won't be shown to any user.
                    </SuiTypography>
                  </SuiBox>
                  <SuiInput placeholder="House number and Street name" {...register("address_1")} />
                  <SuiInput
                    className="mt-2"
                    placeholder="Appartments, suite, unit etc ..."
                    {...register("address_2")}
                  />
                </SuiBox>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  height="100%"
                >
                  <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                    <SuiTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      textTransform="capitalize"
                    >
                      City
                    </SuiTypography>
                  </SuiBox>
                  <SuiInput placeholder="City" {...register("city")} />
                </SuiBox>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  height="100%"
                >
                  <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                    <SuiTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      textTransform="capitalize"
                    >
                      State/Province
                    </SuiTypography>
                  </SuiBox>
                  {countryCode && countryCode.value == "us" ? (
                    <Controller
                      name="province"
                      control={control}
                      render={({ field }) => {
                        return (
                          <SuiSelect
                            {...field}
                            placeholder="State/Province"
                            isSearchable={true}
                            isClearable
                            options={usaStateOptions}
                          />
                        );
                      }}
                    />
                  ) : (
                    <SuiInput {...register("province")} placeholder="State/Province" />
                  )}
                  <ErrorMessage
                    errors={errors}
                    name="province"
                    render={({ message }) => (
                      <SuiTypography
                        variant="text"
                        fontWeight="regular"
                        fontSize="12px"
                        color="error"
                        className="ml-2"
                      >
                        {message}
                      </SuiTypography>
                    )}
                  />
                </SuiBox>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  height="100%"
                >
                  <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                    <SuiTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      textTransform="capitalize"
                    >
                      Country
                    </SuiTypography>
                  </SuiBox>
                  <Controller
                    name="country_code"
                    control={control}
                    render={({ field }) => (
                      <SuiSelect
                        {...field}
                        placeholder="Country"
                        isSearchable={true}
                        isClearable
                        onChange={(e) => {
                          field.onChange(e);
                          handleChangeCountry(e);
                        }}
                        options={countryOptions}
                      />
                    )}
                  />
                </SuiBox>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  height="100%"
                >
                  <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                    <SuiTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      textTransform="capitalize"
                    >
                      Postcode/ZIP
                    </SuiTypography>
                  </SuiBox>
                  <SuiInput placeholder="Postcode/ZIP" {...register("postal_code")} />
                </SuiBox>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  height="100%"
                >
                  <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                    <SuiTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      textTransform="capitalize"
                    >
                      Company Link
                    </SuiTypography>
                  </SuiBox>
                  <SuiInput placeholder="www.yourcomapany.com" {...register("company_link")} />
                </SuiBox>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  height="100%"
                >
                  <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                    <SuiTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      textTransform="capitalize"
                    >
                      Instagram Link
                    </SuiTypography>
                  </SuiBox>
                  <SuiInput placeholder="www.instagram.com" {...register("instagram_link")} />
                </SuiBox>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  height="100%"
                >
                  <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                    <SuiTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      textTransform="capitalize"
                    >
                      Facebook Link
                    </SuiTypography>
                  </SuiBox>
                  <SuiInput placeholder="www.facebook.com" {...register("facebook_link")} />
                </SuiBox>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  height="100%"
                >
                  <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                    <SuiTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      textTransform="capitalize"
                    >
                      Twitter Link
                    </SuiTypography>
                  </SuiBox>
                  <SuiInput placeholder="www.twitter.com" {...register("twitter_link")} />
                </SuiBox>
              </Grid>
            </Grid>
            <SuiBox
              pt={3}
              px={3}
              display="flex"
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              flexDirection={{ xs: "column", sm: "row" }}
            >
              {onPrev ? (
                <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                  <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                    <SuiButton
                      variant="gradient"
                      color={isPrevDisabled ? "light" : "dark"}
                      sx={{ height: "100%" }}
                      onClick={onPrev}
                      disabled={isPrevDisabled}
                    >
                      <ArrowBackIcon fontSize="large" />
                    </SuiButton>
                  </SuiBox>
                </SuiBox>
              ) : (
                <div />
              )}
              <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                  <SuiButton variant="gradient" color="dark" sx={{ height: "100%" }} type="submit">
                    save
                  </SuiButton>
                </SuiBox>
              </SuiBox>
              {onNext ? (
                <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                  <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                    <SuiButton
                      variant="gradient"
                      color={isNextDisabled ? "light" : "dark"}
                      sx={{ height: "100%" }}
                      onClick={onNext}
                      disabled={isNextDisabled}
                    >
                      <ArrowForwardIcon fontSize="large" />
                    </SuiButton>
                  </SuiBox>
                </SuiBox>
              ) : null}
            </SuiBox>
          </SuiBox>
        </Card>
      </SuiBox>
    </div>
  );
};

export default CompanyInfo;
